import type { IOrder, ISuccessApiResponse, RecordValues } from '@wyz/types';
import type { IGeneratedOrder } from '~/purchase/types';
import { IErrorApiResponse } from '@wyz/types';
import {
  CANCELLED_ORDER_STATUSES,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  isEmptyValue,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { omit, pick } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IAcceptOrderPayload,
  IAcceptOrderValidationErrorResponse,
  IPurchaseErrorResponse,
  ISuggestedOffer,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

type Options = {
  getGeneratedOrders?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IGeneratedOrder>>,
      IPurchaseErrorResponse
    >
  > & {
    args: {
      customerId: string;
      tenantCode: string;
      reference: string;
    };
  };
  cancel?: UseMutationOptions<
    ISuccessApiResponse,
    IPurchaseErrorResponse,
    {
      comment?: string;
      status: RecordValues<typeof CANCELLED_ORDER_STATUSES>;
      orderReference: string;
      customerId: string;
      tenantCode: string;
    }
  >;
  accept?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IAcceptOrderValidationErrorResponse,
      PurchaseErrorCodeKeys
    >,
    IAcceptOrderPayload & {
      orderReference: string;
      parentCustomerId: string;
      childCustomerId: string;
    }
  >;
  getSuggestedOffers?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<ISuggestedOffer>>,
      IErrorApiResponse<unknown, PurchaseErrorCodeKeys>
    >
  > & {
    args: {
      reference: string;
      parentCustomerId: string;
    };
  };
  getPendingOrderByReference?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IOrder>, IPurchaseErrorResponse>
  > & {
    args: {
      id: string;
      parentCustomerId: string;
      childCustomerId: string;
    };
  };
};

export const useManageRedistributionOrders = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const cancelMutation = useMutation({
    ...options.cancel,
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/redistribution/customers/${variables.customerId}/orders/${variables.orderReference}/cancel`,
        { method: 'POST', body: JSON.stringify(pick(variables, ['comment'])) },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variables) => [
      PurchaseQueryKeys.orders.processed(variables.customerId),
      PurchaseQueryKeys.orders.pending(variables.customerId),
    ],
  });
  const acceptMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/redistribution/customers/${variables.parentCustomerId}/orders/${variables.orderReference}/accept`,
        {
          method: 'POST',
          body: JSON.stringify(omit(variables, ['orderReference'])),
        },
      ),
    ...options.accept,
    keysToInvalidateOnSuccess: (variables) => [
      PurchaseQueryKeys.orders.processed(variables.parentCustomerId),
      PurchaseQueryKeys.orders.pending(variables.parentCustomerId),
    ],
  });

  const getGeneratedOrdersQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.generatedOrders(
      options.getGeneratedOrders?.args.reference as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IGeneratedOrder>>>(
        `/offers/api/tenants/${
          options.getGeneratedOrders?.args.tenantCode
        }/redistribution/customers/${
          options.getGeneratedOrders?.args.customerId
        }/orders/${
          options.getGeneratedOrders?.args.reference as string
        }/generated`,
      ),
    refetchOnWindowFocus: false,
    ...options.getGeneratedOrders,
    enabled:
      !isEmptyValue(options.getGeneratedOrders?.args.reference) &&
      !isEmptyValue(options.getGeneratedOrders?.args.customerId) &&
      !isEmptyValue(options.getGeneratedOrders?.args.tenantCode) &&
      options.getGeneratedOrders?.enabled,
  });

  const suggestedOffersQuery = useQuery({
    queryKey: PurchaseQueryKeys.redistribution.suggestedOffers(
      options.getSuggestedOffers?.args.reference as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<ISuggestedOffer>>>(
        `/offers/api/tenants/${tenantCode}/redistribution/customers/${options.getSuggestedOffers?.args.parentCustomerId as string}/orders/${
          options.getSuggestedOffers?.args.reference as string
        }/offers-suggested`,
      ),
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    enabled:
      !isEmptyValue(options.getSuggestedOffers?.args.reference) &&
      Boolean(options.getSuggestedOffers?.args.parentCustomerId) &&
      options.getSuggestedOffers?.enabled,
  });

  const getPendingOrderByReferenceQuery = useQuery({
    queryKey: PurchaseQueryKeys.orders.detail(
      options.getPendingOrderByReference?.args.id as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IOrder>>(
        `/offers/api/tenants/${tenantCode}/redistribution/customers/${options.getPendingOrderByReference?.args.parentCustomerId}/orders/pending/${options.getPendingOrderByReference?.args.childCustomerId}/${
          options.getPendingOrderByReference?.args.id as string
        }`,
      ),
    ...options.getPendingOrderByReference,
    retryOnMount: true,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled:
      Boolean(options.getPendingOrderByReference?.args.id) &&
      Boolean(options.getPendingOrderByReference?.args.childCustomerId) &&
      options.getPendingOrderByReference?.enabled,
  });

  return restrictApi(options, {
    getGeneratedOrders: getGeneratedOrdersQuery,
    cancel: cancelMutation,
    accept: acceptMutation,
    getSuggestedOffers: suggestedOffersQuery,
    getPendingOrderByReference: getPendingOrderByReferenceQuery,
  });
};
